import { useQuery } from "react-query";

import { GET_USER_UPCOMING_TASKS, STALE_TIME_1_MINUTES } from "@constants/reactQueryKeys";
import { getUserUpcomingTasks } from "api/users";
import { UpcomingTask } from "api/types/users";

type UserTaskParams = {
  client_id?: number;
};

/**
 * @description Hook to fetch upcoming user tasks by ID using a GET request to `/v3/users/[userId]/upcoming-tasks` endpoint.
 * @param {number|string} userId - The ID of the user to fetch
 * @param {?UserTaskParams} params - Object filters for the tasks.
 * @returns An object containing upcoming user tasks, loading state, and refetch function
 */
export const useGetUserUpcomingTask = (
  userId: number | string,
  params: UserTaskParams | null
): {
  tasks: UpcomingTask[] | null;
  tasksLoading: boolean;
  refetchTasks: () => Promise<unknown>;
  isTasksRefetching: boolean;
} => {
  const { data, isLoading, refetch, isRefetching, ...rest } = useQuery(
    [GET_USER_UPCOMING_TASKS, userId, params?.client_id],
    () => getUserUpcomingTasks(userId, params ?? undefined),
    {
      enabled: Boolean(userId) && Boolean(params?.client_id),
      staleTime: STALE_TIME_1_MINUTES,
      refetchInterval: STALE_TIME_1_MINUTES,
      retry: 3,
    }
  );

  return {
    tasks: (data?.data as UpcomingTask[] | undefined) || null,
    tasksLoading: isLoading,
    refetchTasks: refetch,
    isTasksRefetching: isRefetching,
    ...rest,
  };
};
